import React, { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Card,
  Form,
  FloatingLabel,
  Button,
  Spinner,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft } from "react-feather";

import apiService from "../../services/apiService";
import { useNotification } from "../../components/notification";

// Define validation schema using yup
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  manufacturer: yup.string().required("Manufacturer is required"),
  colourCode: yup.string().required("Colour Code is required"),
  formulaCode: yup.string().required("Formula Code is required"),
  colourGroup: yup.string().required("Colour Group is required"),
  hexCode: yup
    .string()
    .required("Hex Code is required")
    .matches(/^#[0-9A-Fa-f]{6}$/, "Hex Code must be a valid color code"),
  price: yup
    .number()
    .required("Price is required")
    .positive("Price must be positive"),
  isActive: yup.boolean().required("Status is required"),
});

const AddProduct = () => {
  const { productId } = useParams(); // Get product ID from URL for editing
  const didFetch = useRef(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(!!productId); // Check if editing
  const [categoryId, setCategoryId] = useState(""); // Dynamic category ID
  const [manufacturers, setManufacturers] = useState([]); // Dynamic manufacturers

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      manufacturer: "",
      colourCode: "",
      formulaCode: "",
      colourGroup: "",
      hexCode: "",
      price: 0,
      isActive: true,
    },
  });

  // Fetch category and manufacturers
  const fetchCategoryAndManufacturers = useCallback(async () => {
    try {
      setIsPageLoading(true);
      const response = await apiService.get("/category/get-car-search-filter");

      setCategoryId(response.categories[0].id); // Set category ID
      setManufacturers(response.manufacturers); // Set manufacturers
    } catch (error) {
      showNotification("danger", "Failed to fetch category and manufacturers.");
    } finally {
      setIsPageLoading(false);
    }
  }, [showNotification]);

  // Fetch product details for editing
  const fetchProduct = useCallback(async () => {
    try {
      setIsPageLoading(true);
      const response = await apiService.get(`/products/${productId}`);
      reset(response); // Pre-populate form with product data
    } catch (error) {
      showNotification("danger", "Failed to fetch product details.");
    } finally {
      setIsPageLoading(false);
    }
  }, [productId, reset, showNotification]);

  // Fetch data on component mount
  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;
    
    fetchCategoryAndManufacturers();
    if (isEditing) {
      fetchProduct();
    }
  }, [fetchCategoryAndManufacturers, fetchProduct, isEditing]);

  // Handle form submission
  const onSubmit = async (data) => {
    if (!categoryId) {
      showNotification("danger", "Failed to save product.");
      return;
    }
    setIsLoading(true);
    try {
      if (isEditing) {
        // Update existing product
        await apiService.put(`/products/${productId}`, { ...data, categoryId });
        showNotification("success", "Product updated successfully!");
      } else {
        // Create new product
        await apiService.post("/products", { ...data, categoryId });
        showNotification("success", "Product created successfully!");
      }
      navigate("/products"); // Redirect to products list
    } catch (error) {
      showNotification("danger", error || "Failed to save product.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <Card className="p-4 rounded-4">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <h1 className="mb-2 fs-5">
                {isEditing ? "Edit Product" : "Add Product"}
              </h1>
              <p className="mb-0 text-light-emphasis fw-light">
                {isEditing
                  ? "Update the product details below."
                  : "Please enter the product details below."}
              </p>
            </div>
            <Link variant="light" to="/products" className="btn btn-light">
              <ArrowLeft size={18} /> Back
            </Link>
          </div>

          <hr className="mt-3 mb-4" />
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              {/* Name */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Name">
                  <Form.Control
                    type="text"
                    placeholder="Name"
                    {...register("name")}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Manufacturer */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Manufacturer">
                  <Form.Select
                    {...register("manufacturer")}
                    isInvalid={!!errors.manufacturer}
                  >
                    <option value="">Select Manufacturer</option>
                    {manufacturers.map((manufacturer) => (
                      <option key={manufacturer} value={manufacturer}>
                        {manufacturer}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.manufacturer?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Colour Code */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Colour Code">
                  <Form.Control
                    type="text"
                    placeholder="Colour Code"
                    {...register("colourCode")}
                    isInvalid={!!errors.colourCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.colourCode?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Formula Code */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Formula Code">
                  <Form.Control
                    type="text"
                    placeholder="Formula Code"
                    {...register("formulaCode")}
                    isInvalid={!!errors.formulaCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.formulaCode?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Colour Group */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Colour Group">
                  <Form.Control
                    type="text"
                    placeholder="Colour Group"
                    {...register("colourGroup")}
                    isInvalid={!!errors.colourGroup}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.colourGroup?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Hex Code */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Hex Code">
                  <Form.Control
                    type="text"
                    placeholder="Hex Code"
                    {...register("hexCode")}
                    isInvalid={!!errors.hexCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.hexCode?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Price */}
              <Col sm={12} md={6} lg={4} className="mb-3">
                <FloatingLabel label="Price">
                  <Form.Control
                    type="number"
                    placeholder="Price"
                    {...register("price")}
                    isInvalid={!!errors.price}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.price?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Status (Active/Inactive) */}
              <Col sm={12} md={6} lg={4} className="mb-3 d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="isActive"
                  label="Active"
                  {...register("isActive")}
                />
              </Col>
            </Row>

            {/* Submit Button */}
            <Button type="submit" disabled={isLoading} className="btn-main">
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : isEditing ? (
                "Update Product"
              ) : (
                "Add Product"
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default AddProduct;
